import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FadeInSection from '@/components/base/FadeInSection';
import HeroSection from './components/HeroSection';
import TrustBarSection from './components/TrustBarSection';
import { Link } from 'react-router-dom';

const exploreCards = [
  {
    icon: 'ri-building-4-line',
    title: 'About MCT',
    desc: 'Our mission, values, philosophy, and the lifelong relationships that define our approach to care.',
    link: '/about',
    accent: 'primary',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Our Services',
    desc: 'Comprehensive, individualized support across therapeutic programming, life skills, education, and community integration.',
    link: '/services',
    accent: 'accent',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Measurable Outcomes',
    desc: 'Real results and success stories from youth who found their turning point with MCT.',
    link: '/outcomes',
    accent: 'secondary',
  },
  {
    icon: 'ri-shake-hands-line',
    title: 'For Referring Agencies',
    desc: 'Why placement coordinators and agencies across Ontario trust MCT \u2014 and how to know if we\'re the right fit.',
    link: '/for-agencies',
    accent: 'primary',
  },
  {
    icon: 'ri-mail-send-line',
    title: 'Get In Touch',
    desc: 'Direct phone, emergency placement line, office location, and our placement inquiry form.',
    link: '/contact',
    accent: 'accent',
  },
];

export default function Home() {
  return (
    <main>
      <Navbar />
      <HeroSection />
      <TrustBarSection />

      <section className="py-24 md:py-32 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16 md:mb-20">
            <span className="inline-block bg-primary-100 text-primary-700 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider label-font mb-5 animate-hero-fade-in">
              Discover MCT
            </span>
            <h2 className="heading-font text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 max-w-3xl mx-auto leading-tight animate-hero-fade-in" style={{ animationDelay: '0.15s' }}>
              Everything you need, <span className="italic text-primary-600">all in one place.</span>
            </h2>
            <p className="text-foreground-600 mt-5 max-w-2xl mx-auto leading-relaxed text-base md:text-lg animate-hero-fade-in" style={{ animationDelay: '0.3s' }}>
              Whether you&apos;re a placement coordinator evaluating options, or exploring what makes MCT different &mdash; start here.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {exploreCards.map((card, idx) => {
              const accentBorder = card.accent === 'primary' ? 'hover:border-primary-300' : card.accent === 'accent' ? 'hover:border-accent-300' : 'hover:border-secondary-300';
              const accentBg = card.accent === 'primary' ? 'bg-primary-100 text-primary-700 group-hover:bg-primary-200' : card.accent === 'accent' ? 'bg-accent-100 text-accent-700 group-hover:bg-accent-200' : 'bg-secondary-100 text-secondary-700 group-hover:bg-secondary-200';

              return (
                <Link
                  key={card.link}
                  to={card.link}
                  className={`group bg-background-50 rounded-xl p-6 border border-background-200/70 ${accentBorder} transition-all duration-300 flex flex-col cursor-pointer animate-card-stagger`}
                  style={{ animationDelay: `${0.4 + idx * 0.1}s` }}
                >
                  <span className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 transition-colors ${accentBg}`}>
                    <i className={`${card.icon} text-lg`}></i>
                  </span>
                  <h3 className="font-semibold text-foreground-900 mb-2 label-font text-sm group-hover:text-primary-600 transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed flex-1 mb-4">{card.desc}</p>
                  <span className="flex items-center gap-1 text-xs font-medium text-foreground-400 group-hover:text-primary-600 transition-colors label-font">
                    Learn more
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <FadeInSection>
        <section className="py-24 md:py-32 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <span className="inline-block bg-accent-100 text-accent-800 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider label-font mb-5">
              Start a Conversation
            </span>
            <h2 className="heading-font text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-tight mb-5">
              Ready to make a <span className="italic text-primary-600">difference?</span>
            </h2>
            <p className="text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-8">
              Every young person deserves a turning point. If you&apos;re a placing agency looking for a partner who builds futures, we&apos;d love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="bg-primary-500 text-white px-8 py-3.5 rounded-full text-sm font-semibold label-font hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Request Placement
              </Link>
              <a
                href="tel:12892046406"
                className="flex items-center gap-2 bg-background-100 text-foreground-800 px-8 py-3.5 rounded-full text-sm font-semibold label-font hover:bg-background-200 transition-all duration-300 cursor-pointer whitespace-nowrap border border-background-200/70"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </span>
                Call 1 289 204 6406
              </a>
            </div>
          </div>
        </section>
      </FadeInSection>

      <Footer />
    </main>
  );
}